/**
 * MontaTech v2 - Bezier Utilities
 * Conversão de curvas Bezier para polilinhas
 */

import type { Point, RSegment, RPath, RCurve } from '../types';

/**
 * Calcula ângulo entre dois pontos
 */
export function calculateAngle(x1: number, y1: number, x2: number, y2: number): number {
  if (x1 === x2) {
    return y2 > y1 ? Math.PI : 0;
  }
  let angle = Math.atan((y2 - y1) / (x2 - x1)) + Math.PI / 2;
  if (x2 - x1 < 0) angle += Math.PI;
  return angle;
}

/**
 * Calcula diferença de ângulos normalizada para [-π, π]
 */
export function deltaAngle(a1: number, a2: number): number {
  let delta = a2 - a1;
  if (delta > Math.PI) delta -= 2 * Math.PI;
  else if (delta < -Math.PI) delta += 2 * Math.PI;
  return delta;
}

/**
 * Calcula profundidade de recursão baseado na curvatura
 */
export function calculateRecursionDepth(
  x1: number, y1: number,
  x2: number, y2: number,
  x3: number, y3: number,
  x4: number, y4: number
): number {
  const a1 = calculateAngle(x1, y1, x2, y2);
  const a = calculateAngle(x2, y2, x3, y3);
  const a2 = calculateAngle(x3, y3, x4, y4);

  let da1 = Math.abs(deltaAngle(a, a1));
  let da2 = Math.abs(deltaAngle(a2, a));

  // Se pontos de controle estão sobre os nós, ignorar curvatura
  if (x2 === x1 && y2 === y1) da1 = 0;
  if (x3 === x4 && y3 === y4) da2 = 0;

  const totalCurvature = da1 + da2;

  if (totalCurvature < 0.01) return 0;
  if (totalCurvature < 0.05) return 1;
  if (totalCurvature < 0.26) return 2;
  if (totalCurvature < 0.9) return 3;
  if (totalCurvature < 2.2) return 4;
  return 5;
}

/**
 * Subdivisão recursiva de curva Bezier (Algoritmo de De Casteljau)
 */
export function subdivideBezier(
  x1: number, y1: number,
  x2: number, y2: number,
  x3: number, y3: number,
  x4: number, y4: number,
  depth: number,
  maxDepth: number,
  points: Point[]
): void {
  if (depth >= maxDepth) {
    // Adicionar ponto final
    points.push({ x: x4, y: y4 });
    return;
  }

  // Pontos médios (De Casteljau)
  const x12 = (x1 + x2) / 2;
  const y12 = (y1 + y2) / 2;
  const x23 = (x2 + x3) / 2;
  const y23 = (y2 + y3) / 2;
  const x34 = (x3 + x4) / 2;
  const y34 = (y3 + y4) / 2;

  const x123 = (x12 + x23) / 2;
  const y123 = (y12 + y23) / 2;
  const x234 = (x23 + x34) / 2;
  const y234 = (y23 + y34) / 2;

  const x1234 = (x123 + x234) / 2;
  const y1234 = (y123 + y234) / 2;

  // Subdividir recursivamente
  subdivideBezier(x1, y1, x12, y12, x123, y123, x1234, y1234, depth + 1, maxDepth, points);
  subdivideBezier(x1234, y1234, x234, y234, x34, y34, x4, y4, depth + 1, maxDepth, points);
}

/**
 * Converte um path de segmentos Bezier para uma polilinha
 */
export function pathToPolyline(path: RPath, maxLength: number): Point[] {
  const points: Point[] = [];
  
  if (path.segments.length === 0) return points;

  // Primeiro ponto
  const firstSeg = path.segments[0];
  points.push({
    x: firstSeg.SNPx * maxLength,
    y: firstSeg.SNPy * maxLength
  });

  // Processar cada segmento
  path.segments.forEach((seg, i) => {
    const startX = seg.SNPx * maxLength;
    const startY = seg.SNPy * maxLength;
    const cp1X = seg.SCPx * maxLength;
    const cp1Y = seg.SCPy * maxLength;
    const cp2X = seg.ECPx * maxLength;
    const cp2Y = seg.ECPy * maxLength;

    // Determinar ponto final
    let endX: number, endY: number;
    if (i === path.segments.length - 1 && (path as any).closed !== false) {
      // Último segmento de path fechado: fechar no primeiro ponto
      endX = points[0].x;
      endY = points[0].y;
    } else if (i === path.segments.length - 1) {
      // Último segmento de path aberto: usar ECP como endpoint
      endX = cp2X;
      endY = cp2Y;
    } else {
      const nextSeg = path.segments[i + 1];
      endX = nextSeg.SNPx * maxLength;
      endY = nextSeg.SNPy * maxLength;
    }

    // Calcular profundidade de subdivisão baseado na curvatura
    const maxRecurse = calculateRecursionDepth(
      startX, startY, cp1X, cp1Y, cp2X, cp2Y, endX, endY
    );

    // Subdividir Bezier
    subdivideBezier(
      startX, startY, cp1X, cp1Y, cp2X, cp2Y, endX, endY,
      0, maxRecurse, points
    );
  });

  return points;
}

/**
 * Converte uma curva completa para array de polilinhas
 */
export function curveToPolylines(curve: RCurve): Point[][] {
  const result: Point[][] = [];
  
  // maxLength já está normalizado (0-65530 → real units)
  const scale = curve.maxLength > 0 ? curve.maxLength : 1 / 65530;
  
  curve.paths.forEach(path => {
    const points = pathToPolyline(path, scale);
    if (points.length > 0) {
      result.push(points);
    }
  });

  return result;
}

/**
 * Aplica rotação a um ponto
 */
export function rotatePoint(point: Point, angle: number): Point {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return {
    x: point.x * cos + point.y * sin,
    y: point.y * cos - point.x * sin
  };
}

/**
 * Aplica rotação a um array de pontos
 */
export function rotatePoints(points: Point[], angle: number): Point[] {
  return points.map(p => rotatePoint(p, angle));
}

